import React from "react";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import WarningIcon from "@mui/icons-material/Warning";
import ErrorIcon from "@mui/icons-material/Error";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import {
  ArmedStatusTone,
  getArmedStatusPresentation,
} from "../SitesPage/SitesPageUtils/armedStatusPresentation";

const TONE_STYLES: Record<ArmedStatusTone, { bg: string; fg: string }> = {
  armed: { bg: "#e6f4ea", fg: "#1b5e20" },
  manual: { bg: "#e6f4ea", fg: "#1b5e20" },
  transitional: { bg: "#fff3e0", fg: "#a35300" },
  warning: { bg: "#fff3e0", fg: "#a35300" },
  error: { bg: "#fdecea", fg: "#b71c1c" },
  disarmed: { bg: "#eeeeee", fg: "#424242" },
  unknown: { bg: "#eeeeee", fg: "#616161" },
};

const toneIcon = (status: string, tone: ArmedStatusTone): JSX.Element => {
  if (status === "ARMING") return <AutorenewIcon />;
  if (status === "DISARMING") return <HourglassTopIcon />;
  switch (tone) {
    case "armed":
      return <CheckCircleIcon />;
    case "manual":
    case "warning":
      return <WarningIcon />;
    case "error":
      return <ErrorIcon />;
    case "disarmed":
      return <CancelIcon />;
    default:
      return <HelpOutlineIcon />;
  }
};

interface ArmedStatusChipProps {
  armedStatus?: string;
  armed?: boolean;
  active?: boolean;
}

/**
 * Labelled arm-state chip for the site About table (SUP-515).
 *
 * Says in words what the toggle and icon only implied: "Arming…",
 * "Disarming…", "Armed – not running", "Armed (manual)", … with the full
 * explanation on hover.
 */
export const ArmedStatusChip = ({
  armedStatus,
  armed,
  active = true,
}: ArmedStatusChipProps) => {
  const presentation = getArmedStatusPresentation(armedStatus, armed, active);
  const colors = TONE_STYLES[presentation.tone];
  return (
    <Tooltip title={presentation.description} placement="top">
      <Chip
        size="small"
        icon={toneIcon(presentation.status, presentation.tone)}
        label={presentation.label}
        data-testid="armed-status-chip"
        data-armed-status={presentation.status}
        sx={{
          backgroundColor: colors.bg,
          color: colors.fg,
          fontFamily: "Mulish",
          fontWeight: 700,
          "& .MuiChip-icon": { color: colors.fg },
        }}
      />
    </Tooltip>
  );
};
